import { useState, useRef, useCallback } from 'react';
import Papa from 'papaparse';
import { supabase } from '../lib/supabase';
import { getLastDayOfMonth } from '../lib/utils';
import { getPreviousMonthValue } from './useBudgetData';

export const useTransactionMutations = ({
  user,
  currentMonth,
  categories,
  setTransactions,
  pendingRecurring,
  setPendingRecurring,
  setLastUsedCategory,
  setToast,
}) => {
  const [pendingDelete, setPendingDelete] = useState(null);
  const isSubmitting = useRef(false);
  const lastMutationAt = useRef(0);
  const txnsRef = useRef([]);

  // Keep ref in sync (avoids stale closures without listing transactions as dep)
  const syncTxns = useCallback((txns) => { txnsRef.current = txns; }, []);

  const getDefaultTransactionDate = useCallback(() => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    const todayMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
    return currentMonth === todayMonth ? todayStr : `${currentMonth}-01`;
  }, [currentMonth]);

  const buildPayload = useCallback((form) => {
    const amount = parseFloat(form.amount);
    const expenseAmount = amount < 0 ? amount : -Math.abs(amount);
    return {
      user_id: user.id,
      category_id: form.category_id || null,
      amount: expenseAmount,
      date: form.date,
      payee: form.description || form.payee || '',
      memo: '',
      is_recurring: form.is_recurring || false,
      recurrence_day: form.is_recurring ? new Date(form.date).getDate() : null,
      recurring_template_id: null,
    };
  }, [user]);

  const addTransaction = useCallback(async (form, onSuccess) => {
    if (isSubmitting.current) return;
    const now = Date.now();
    if (now - lastMutationAt.current < 500) return;
    isSubmitting.current = true;
    lastMutationAt.current = now;
    try {
      const { data, error } = await supabase.from('transactions').insert([buildPayload(form)]).select();
      if (error) { setToast({ message: 'Add failed', type: 'error' }); return; }
      if (data?.[0]) {
        const txn = data[0];
        if (txn.date.substring(0, 7) === currentMonth) {
          setTransactions(prev => [txn, ...prev]);
        }
      }
      if (form.category_id) {
        const cat = categories.find(c => c.id === form.category_id);
        if (cat) {
          setLastUsedCategory(cat);
          localStorage.setItem('lastUsedCategoryId', cat.id);
        }
      }
      setToast({ message: form.is_recurring ? 'Added! Repeats monthly.' : 'Added!', type: 'success' });
      onSuccess?.();
    } finally {
      isSubmitting.current = false;
    }
  }, [currentMonth, categories, buildPayload, setTransactions, setLastUsedCategory, setToast]);

  const simpleModeExpense = useCallback(async (expenseData) => {
    const { data, error } = await supabase.from('transactions').insert([buildPayload(expenseData)]).select();
    if (error) throw error;
    if (data?.[0] && data[0].date.substring(0, 7) === currentMonth) {
      setTransactions(prev => [data[0], ...prev]);
    }
  }, [currentMonth, buildPayload, setTransactions]);

  const deleteTransaction = useCallback((idOrTxn) => {
    const id = typeof idOrTxn === 'object' ? idOrTxn.id : idOrTxn;
    const txn = typeof idOrTxn === 'object' ? idOrTxn : txnsRef.current.find(t => t.id === id);
    setPendingDelete({
      id,
      isRecurring: txn?.is_recurring || false,
      category_id: txn?.category_id,
      recurrence_day: txn?.recurrence_day,
      stopRecurring: false,
    });
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    const { id, isRecurring, category_id, recurrence_day, stopRecurring } = pendingDelete;

    if (isRecurring && stopRecurring) {
      // Find the specific source transaction from the previous month only
      const prevMonth = getPreviousMonthValue(currentMonth);
      const { data: sourceTxns } = await supabase
        .from('transactions')
        .select('id')
        .eq('user_id', user.id)
        .eq('is_recurring', true)
        .eq('category_id', category_id)
        .eq('recurrence_day', recurrence_day)
        .gte('date', `${prevMonth}-01`)
        .lte('date', getLastDayOfMonth(prevMonth))
        .neq('id', id)
        .limit(1);

      if (sourceTxns?.length) {
        await supabase.from('transactions')
          .update({ is_recurring: false, recurrence_day: null })
          .eq('id', sourceTxns[0].id);
      }
    }

    const { error } = await supabase.from('transactions').delete().eq('id', id);
    if (error) {
      setToast({ message: 'Delete failed', type: 'error' });
    } else {
      setTransactions(prev => prev.filter(t => t.id !== id));
      setToast({ message: 'Deleted!', type: 'info' });
    }
    setPendingDelete(null);
  }, [pendingDelete, currentMonth, user, setTransactions, setToast]);

  const confirmRecurring = useCallback(async (sourceTxn) => {
    const [year, monthNum] = currentMonth.split('-').map(Number);
    const lastDay = new Date(year, monthNum, 0).getDate();
    const date = `${currentMonth}-${String(Math.min(sourceTxn.recurrence_day, lastDay)).padStart(2, '0')}`;
    const { data, error } = await supabase.from('transactions').insert({
      user_id: user.id,
      category_id: sourceTxn.category_id,
      amount: sourceTxn.amount,
      date,
      payee: sourceTxn.payee || '',
      is_recurring: true,
      recurrence_day: sourceTxn.recurrence_day,
    }).select();
    if (error) { setToast({ message: 'Add failed', type: 'error' }); return; }
    if (data?.[0]) setTransactions(prev => [data[0], ...prev]);
    setPendingRecurring(prev => prev.filter(t => t.id !== sourceTxn.id));
    setToast({ message: 'Added!', type: 'success' });
  }, [currentMonth, user, setTransactions, setPendingRecurring, setToast]);

  const denyRecurring = useCallback(async (sourceTxn) => {
    const { error } = await supabase.from('transactions')
      .update({ is_recurring: false, recurrence_day: null })
      .eq('id', sourceTxn.id);
    if (error) { setToast({ message: 'Update failed', type: 'error' }); return; }
    setPendingRecurring(prev => prev.filter(t => t.id !== sourceTxn.id));
  }, [setPendingRecurring, setToast]);

  const confirmAllRecurring = useCallback(async () => {
    if (!pendingRecurring.length) return;
    const [year, monthNum] = currentMonth.split('-').map(Number);
    const lastDay = new Date(year, monthNum, 0).getDate();
    const inserts = pendingRecurring.map(src => ({
      user_id: user.id,
      category_id: src.category_id,
      amount: src.amount,
      date: `${currentMonth}-${String(Math.min(src.recurrence_day, lastDay)).padStart(2, '0')}`,
      payee: src.payee || '',
      is_recurring: true,
      recurrence_day: src.recurrence_day,
    }));
    const { data, error } = await supabase.from('transactions').insert(inserts).select();
    if (error) { setToast({ message: 'Add failed', type: 'error' }); return; }
    if (data) setTransactions(prev => [...data, ...prev]);
    setPendingRecurring([]);
    setToast({ message: `${inserts.length} added!`, type: 'success' });
  }, [currentMonth, pendingRecurring, user, setTransactions, setPendingRecurring, setToast]);

  const importCSV = useCallback((file, selectedCategory, onSuccess) => {
    if (!file) return;
    Papa.parse(file, {
      header: true,
      complete: async (results) => {
        const rows = results.data.filter(row => {
          if (!row.date || !row.amount) return false;
          if (!/^\d{4}-\d{2}-\d{2}$/.test(String(row.date).trim())) return false;
          if (!isFinite(parseFloat(row.amount))) return false;
          return true;
        });

        if (!rows.length) { setToast({ message: 'No valid rows', type: 'error' }); return; }

        const importRows = rows.map(row => ({
          user_id: user.id,
          category_id: selectedCategory?.id || null,
          amount: parseFloat(row.amount),
          date: String(row.date).trim(),
          payee: [row.payee, row.description, row.memo, row.notes].filter(Boolean).join(' ').trim() || '',
          memo: '',
        }));

        const { data, error } = await supabase.from('transactions').insert(importRows).select();
        if (error) { setToast({ message: 'Import failed', type: 'error' }); return; }
        if (data) {
          const inMonth = data.filter(t => t.date.substring(0, 7) === currentMonth);
          if (inMonth.length) setTransactions(prev => [...inMonth, ...prev]);
        }
        setToast({ message: `${importRows.length} imported!`, type: 'success' });
        onSuccess?.();
      },
      error: () => setToast({ message: 'Parse error', type: 'error' }),
    });
  }, [user, currentMonth, setTransactions, setToast]);

  return {
    pendingDelete,
    setPendingDelete,
    syncTxns,
    getDefaultTransactionDate,
    addTransaction,
    simpleModeExpense,
    deleteTransaction,
    confirmDelete,
    confirmRecurring,
    denyRecurring,
    confirmAllRecurring,
    importCSV,
    isSubmitting,
  };
};
